import type { RadioStation } from '@/types';

const AZURACAST_URL = import.meta.env.VITE_AZURACAST_URL || '';

interface AzuraCastMount {
  id: number;
  url: string;
  bitrate: number;
  format: string;
  is_default: boolean;
  name?: string;
}

interface AzuraCastStation {
  station: {
    id: number;
    name: string;
    shortcode: string;
    description: string;
    listen_url: string;
    mounts: AzuraCastMount[];
  };
  now_playing?: {
    song?: {
      artist: string;
      title: string;
      art?: string;
    };
  };
  is_online?: boolean;
}

function transformToRadioStation(np: AzuraCastStation): RadioStation {
  const s = np.station;
  const defaultMount = s.mounts?.find((m) => m.is_default) ?? s.mounts?.[0];
  const streamUrl = defaultMount?.url ?? s.listen_url;

  return {
    id: `azuracast-${s.id}`,
    name: s.name,
    streamUrl,
    genre: 'Balkan Mix',
    description: s.description || s.name,
    artwork: np.now_playing?.song?.art || '',
    isActive: np.is_online !== false,
    bitrate: defaultMount?.bitrate ?? 128,
    location: '',
  };
}

/** Dohvata listu stanica iz AzuraCast Now Playing API */
export async function fetchStreamsFromAzuraCast(): Promise<RadioStation[]> {
  if (!AZURACAST_URL) return [];

  const base = AZURACAST_URL.replace(/\/$/, '');
  const url = `${base}/api/nowplaying`;

  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('AzuraCast API error');
    const data = await res.json();

    const list = Array.isArray(data) ? data : data.data ? data.data : [data];
    if (!Array.isArray(list) || list.length === 0) return [];

    return list
      .filter((np: AzuraCastStation) => np.station && np.is_online !== false)
      .map((np: AzuraCastStation) => transformToRadioStation(np));
  } catch {
    return [];
  }
}

/** Dohvata Now Playing za jednu stanicu (artist, title, art) */
export async function fetchNowPlaying(
  shortcode: string
): Promise<{ artist: string; title: string; art?: string } | null> {
  if (!AZURACAST_URL) return null;

  const base = AZURACAST_URL.replace(/\/$/, '');
  const url = `${base}/api/nowplaying/${shortcode}`;

  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();

    const song = data.now_playing?.song;
    if (!song) return null;

    return {
      artist: song.artist || '',
      title: song.title || '',
      art: song.art,
    };
  } catch {
    return null;
  }
}
